import type { RowConsultingResponse } from '@/Network/Types/Responses/StationResponses'
import {
  COVERAGE_STATUS_LABELS,
  hasCoverageContract,
  isUnidentifiedCoverage,
  resolveCoverageStatus
} from '@/utils/coverage'
import { formatCurrency, formatCurrencyOrDash } from '@/utils/formatters'

export const UNIDENTIFIED_NAME_LABEL = 'Ocupado s/ ident.'

export interface ConsultHeader {
  key: string
  align: 'start'
  title: string
  sortable: false
  value: (row: RowConsultingResponse) => string
}

/**
 * Valor de referência do plantão ocupado sem identificação: o BE manda
 * `referenceValue` e mantém `value`/`modifiedValue` preenchidos (fallback p/ BE sem o campo).
 */
const referenceValueOf = (row: RowConsultingResponse) =>
  row.referenceValue ?? row.modifiedValue ?? row.value

export const CONSULT_COVERAGE_HEADERS: ConsultHeader[] = [
  {
    key: 'coverageStatus',
    align: 'start',
    title: 'Status da cobertura',
    sortable: false,
    value: (row) => COVERAGE_STATUS_LABELS[resolveCoverageStatus(row)]
  },
  {
    key: 'coverageResponsibleName',
    align: 'start',
    title: 'Responsável',
    sortable: false,
    value: (row) => row.coverageResponsibleName || '—'
  },
  {
    key: 'referenceValue',
    align: 'start',
    title: 'Valor ref.',
    sortable: false,
    value: (row) =>
      isUnidentifiedCoverage(row) ? formatCurrencyOrDash(referenceValueOf(row)) : '—'
  }
]

/** Colunas novas só quando o backend manda `coverageStatus` (BE antigo = tabela de hoje). */
export function withCoverageHeaders<T extends { key: string }>(
  headers: T[],
  rows: RowConsultingResponse[]
): Array<T | ConsultHeader> {
  if (!rows.some((row) => hasCoverageContract(row))) return headers
  const nameIndex = headers.findIndex((header) => header.key === 'name')
  const at = nameIndex >= 0 ? nameIndex + 1 : headers.length
  return [...headers.slice(0, at), ...CONSULT_COVERAGE_HEADERS, ...headers.slice(at)]
}

/** Coluna "Valor": UNIDENTIFIED fica fora dos totais (o valor vai para "Valor ref."). */
export function consultValueCell(row: RowConsultingResponse): string {
  if (isUnidentifiedCoverage(row)) return '—'
  return `R$ ${formatCurrency(row.modifiedValue ?? 0)}`
}
